import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Plus, Edit, Trash2, ArrowLeft, Eye, EyeOff, GripVertical, ChevronUp, ChevronDown, Save, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminVolunteerOpportunities = () => {
  const [opportunities, setOpportunities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingOpportunity, setEditingOpportunity] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [reorderedOpportunities, setReorderedOpportunities] = useState([]);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    time: '',
    description: '',
    note: '',
    is_active: true
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchOpportunities();
  }, [navigate]);

  const fetchOpportunities = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/volunteer-opportunities?active_only=false`);
      setOpportunities(response.data);
    } catch (error) {
      toast.error('Failed to fetch volunteer opportunities');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    try {
      const submitData = {
        ...formData,
        note: formData.note || null
      };

      if (editingOpportunity) {
        await axios.put(
          `${BACKEND_URL}/api/volunteer-opportunities/${editingOpportunity.id}`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Opportunity updated successfully');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/volunteer-opportunities`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Opportunity created successfully');
      }
      setShowForm(false);
      setEditingOpportunity(null);
      resetForm();
      fetchOpportunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save opportunity');
    }
  };

  const handleEdit = (opportunity) => {
    setEditingOpportunity(opportunity);
    setFormData({
      title: opportunity.title,
      time: opportunity.time,
      description: opportunity.description,
      note: opportunity.note || '',
      is_active: opportunity.is_active
    });
    setShowForm(true);
  };

  const handleDelete = async (opportunityId) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(
        `${BACKEND_URL}/api/volunteer-opportunities/${opportunityId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Opportunity deleted successfully');
      setShowDeleteConfirm(null);
      fetchOpportunities();
    } catch (error) {
      toast.error('Failed to delete opportunity');
    }
  };

  const toggleActive = async (opportunity) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.put(
        `${BACKEND_URL}/api/volunteer-opportunities/${opportunity.id}`,
        { is_active: !opportunity.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Opportunity ${opportunity.is_active ? 'deactivated' : 'activated'}`);
      fetchOpportunities();
    } catch (error) {
      toast.error('Failed to update opportunity');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      time: '',
      description: '',
      note: '',
      is_active: true
    });
  };

  // Reorder functions
  const enterReorderMode = () => {
    setReorderedOpportunities([...opportunities]);
    setIsReorderMode(true);
  };

  const exitReorderMode = () => {
    setIsReorderMode(false);
    setReorderedOpportunities([]);
  };

  const moveOpportunity = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= reorderedOpportunities.length) return;
    
    const newOpportunities = [...reorderedOpportunities];
    [newOpportunities[index], newOpportunities[newIndex]] = [newOpportunities[newIndex], newOpportunities[index]];
    setReorderedOpportunities(newOpportunities);
  };

  const saveOrder = async () => {
    const token = localStorage.getItem('admin_token');
    setIsSavingOrder(true);
    
    try {
      const orders = reorderedOpportunities.map((opp, index) => ({
        opportunity_id: opp.id,
        display_order: index
      }));
      
      await axios.post(
        `${BACKEND_URL}/api/volunteer-opportunities/reorder`,
        orders,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Order saved successfully');
      setIsReorderMode(false);
      fetchOpportunities();
    } catch (error) {
      toast.error('Failed to save order');
    } finally {
      setIsSavingOrder(false);
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-volunteer-opportunities-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => navigate('/admin/dashboard')}
              variant="outline"
              className="border-[#007A7A] text-[#007A7A]"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">
              {isReorderMode ? 'Reorder Opportunities' : 'Volunteer Opportunities'}
            </h1>
          </div>
          {!isReorderMode && (
            <div className="flex space-x-3">
              <Button
                onClick={enterReorderMode}
                variant="outline"
                className="border-[#007A7A] text-[#007A7A]"
                data-testid="reorder-btn"
                disabled={opportunities.length < 2}
              >
                <GripVertical className="w-4 h-4 mr-2" />
                Reorder
              </Button>
              <Button
                onClick={() => {
                  setShowForm(true);
                  setEditingOpportunity(null);
                  resetForm();
                }}
                className="bg-[#007A7A] hover:bg-[#007A7A]/90"
                data-testid="add-opportunity-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Opportunity
              </Button>
            </div>
          )}
          {isReorderMode && (
            <div className="flex space-x-3">
              <Button
                onClick={exitReorderMode}
                variant="outline"
                className="border-gray-400 text-gray-600"
                data-testid="cancel-reorder-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={saveOrder}
                disabled={isSavingOrder}
                className="bg-green-600 hover:bg-green-700"
                data-testid="save-order-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {isSavingOrder ? 'Saving...' : 'Save Order'}
              </Button>
            </div>
          )}
        </div>

        {/* Normal List View */}
        {!isReorderMode && (
          <div className="space-y-4" data-testid="opportunities-list">
            {opportunities.length === 0 ? (
              <Card className="py-16">
                <div className="text-center text-gray-500">
                  <p className="mb-4">No volunteer opportunities yet.</p>
                  <Button
                    onClick={() => setShowForm(true)}
                    className="bg-[#007A7A] hover:bg-[#007A7A]/90"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Opportunity
                  </Button>
                </div>
              </Card>
            ) : (
              opportunities.map((opportunity) => (
                <Card key={opportunity.id} className="hover:shadow-lg transition-shadow duration-300" data-testid={`opportunity-${opportunity.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-semibold text-gray-900">{opportunity.title}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            opportunity.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {opportunity.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 mb-2">
                          Time: {opportunity.time}
                        </div>
                        <p className="text-gray-600">{opportunity.description}</p>
                        {opportunity.note && (
                          <p className="text-sm text-amber-700 mt-2 italic">Note: {opportunity.note}</p>
                        )}
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleActive(opportunity)}
                          className={opportunity.is_active ? 'border-yellow-600 text-yellow-600' : 'border-green-600 text-green-600'}
                          data-testid={`toggle-active-${opportunity.id}`}
                        >
                          {opportunity.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleEdit(opportunity)}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid={`edit-${opportunity.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowDeleteConfirm(opportunity)}
                          className="border-red-600 text-red-600 hover:bg-red-50"
                          data-testid={`delete-${opportunity.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Reorder Mode View */}
        {isReorderMode && (
          <div className="space-y-2" data-testid="reorder-list">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-blue-800 text-sm">
                <strong>Reorder Mode:</strong> Use the up/down arrows to change the order. Click "Save Order" when done.
              </p>
            </div>
            {reorderedOpportunities.map((opportunity, index) => (
              <Card key={opportunity.id} className="border-2 border-dashed border-gray-300 hover:border-[#007A7A]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-[#007A7A] text-white rounded-full flex items-center justify-center font-bold text-sm">
                      {index + 1}
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => moveOpportunity(index, -1)}
                        disabled={index === 0}
                        className="h-7 w-7 p-0"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => moveOpportunity(index, 1)}
                        disabled={index === reorderedOpportunities.length - 1}
                        className="h-7 w-7 p-0"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">{opportunity.title}</h3>
                      <p className="text-sm text-gray-500">{opportunity.time}</p>
                    </div>
                    <GripVertical className="w-5 h-5 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingOpportunity ? 'Edit Opportunity' : 'Add Volunteer Opportunity'}</DialogTitle>
              <DialogDescription>
                {editingOpportunity ? 'Update the details below.' : 'Fill in the details for the new opportunity.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="e.g., Food Market"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A] focus:border-transparent"
                  data-testid="opportunity-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Time Commitment *</label>
                <input
                  type="text"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  required
                  placeholder="e.g., As needed, Weekends, 2 hours/week"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A] focus:border-transparent"
                  data-testid="opportunity-time-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows="3"
                  placeholder="Describe the volunteer opportunity..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A] focus:border-transparent"
                  data-testid="opportunity-description-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Note (Optional)</label>
                <input
                  type="text"
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  placeholder="e.g., Requires background check"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A] focus:border-transparent"
                  data-testid="opportunity-note-input"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 text-[#007A7A] focus:ring-[#007A7A] border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                  Active (visible on Volunteer page)
                </label>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#007A7A] hover:bg-[#007A7A]/90" data-testid="save-opportunity-btn">
                  {editingOpportunity ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Delete Opportunity?</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{showDeleteConfirm?.title}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => handleDelete(showDeleteConfirm.id)}
                data-testid="confirm-delete-btn"
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminVolunteerOpportunities;
